import MoreHoriz from '@mui/icons-material/MoreHoriz';
import NotificationsNoneOutlined from '@mui/icons-material/NotificationsNoneOutlined';
import NotificationsOffOutlined from '@mui/icons-material/NotificationsOffOutlined';
import { buttonBaseClasses, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import { buttonClasses } from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { ReactNode, useRef, useState } from 'react';

import Menu, { MenuHandler } from '~components/Menu/Menu';
import useMuteToggle from '~components/Messaging/useMuteToggle';
import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { MessagingChannel } from '~hooks/useMessaging/models';

export const ROW_DETAIL_REPLACED_BY_ACTIONS = { 'data-replaced-by-row-actions': true };

const REPLACED_DETAIL_SELECTOR = '[data-replaced-by-row-actions]';
const ACTIONS_BUTTON_CLASS = 'messaging-channel-row-actions-button';

// Fits the 20px icon with a hover ring, small enough to sit on a row's title line.
const ACTIONS_BUTTON_SIZE_IN_PX = 28;
const MENU_ITEM_ICON_SIZE_IN_PX = 16;
const SAVING_SPINNER_SIZE_IN_PX = 16;
const MENU_ITEM_FONT_SIZE_IN_PX = 12;

type MessagingChannelRowActionsProps = {
  channel: MessagingChannel;
  children: ReactNode;
  buttonInset: { right: number; top: number };
  // A rail with one tab stop keeps every other row's button out of the Tab order.
  isTabbable?: boolean;
};

export default function MessagingChannelRowActions({
  buttonInset,
  channel,
  children,
  isTabbable = true,
}: MessagingChannelRowActionsProps) {
  const theme = useTheme();
  const { isSaving, label, toggleMuted } = useMuteToggle(channel);
  const menuRef = useRef<MenuHandler>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const showActionsSx = {
    [`& .${ACTIONS_BUTTON_CLASS}`]: { opacity: 1 },
    [`& ${REPLACED_DETAIL_SELECTOR}`]: { visibility: 'hidden' },
  };

  const touchActionsSx = {
    [`& .${ACTIONS_BUTTON_CLASS}`]: { opacity: 1 },
    [`& ${REPLACED_DETAIL_SELECTOR}`]: {
      mr: `${ACTIONS_BUTTON_SIZE_IN_PX}px`,
      visibility: 'visible',
    },
  };

  return (
    <Box
      data-test-id="messaging-channel-row-actions"
      sx={{
        position: 'relative',
        '&:hover, &:focus-within': showActionsSx,
        ...(isMenuOpen || isSaving ? showActionsSx : {}),
        '@media (hover: none)': touchActionsSx,
      }}
    >
      {children}

      <Menu
        menuTrigger={
          <SmallButton
            aria-label={t('messaging.conversation_actions')}
            className={ACTIONS_BUTTON_CLASS}
            color="brandV2TreadGray3"
            startIcon={
              isSaving ? (
                <CircularProgress size={SAVING_SPINNER_SIZE_IN_PX} />
              ) : (
                <MoreHoriz />
              )
            }
            sx={{
              opacity: 0,
              position: 'absolute',
              right: buttonInset.right,
              top: buttonInset.top,
              [`&.${buttonBaseClasses.root}`]: {
                border: 'solid 1px transparent',
                borderRadius: '50%',
                height: ACTIONS_BUTTON_SIZE_IN_PX,
                minWidth: ACTIONS_BUTTON_SIZE_IN_PX,
                p: 0,
                width: ACTIONS_BUTTON_SIZE_IN_PX,
                '&:hover': { borderColor: theme.brandV2.colors.treadGray3 },
                [`.${buttonClasses.startIcon}`]: { mx: 0 },
              },
            }}
            tabIndex={isTabbable ? undefined : -1}
            variant="text"
          />
        }
        onOpenStateChanged={setIsMenuOpen}
        ref={menuRef}
      >
        <MenuItem
          disabled={isSaving}
          onClick={(event) => {
            event.stopPropagation();
            menuRef.current?.onClose?.();
            void toggleMuted();
          }}
        >
          <Box sx={{ alignItems: 'center', display: 'flex', gap: 1, width: '100%' }}>
            <MenuItemIcon isMuted={channel.muted} />

            <Typography
              sx={{
                color: theme.brandV2.colors.treadBlack,
                fontSize: `${MENU_ITEM_FONT_SIZE_IN_PX}px`,
              }}
            >
              {label}
            </Typography>
          </Box>
        </MenuItem>
      </Menu>
    </Box>
  );
}

function MenuItemIcon({ isMuted }: { isMuted: boolean }) {
  const theme = useTheme();
  const iconSx = {
    color: theme.brandV2.colors.treadBlack,
    fontSize: `${MENU_ITEM_ICON_SIZE_IN_PX}px`,
  };

  return isMuted ? (
    <NotificationsOffOutlined sx={iconSx} />
  ) : (
    <NotificationsNoneOutlined sx={iconSx} />
  );
}
